/* oxlint-disable max-lines-per-function */
import { startTransition, useEffect, useMemo, useRef, useState, type VideoHTMLAttributes } from 'react';
import { VideoPlayer } from './VideoStream';
import { useHasInteractedIfNeeded } from './useHasInteractedIfNeeded';

type UseVideoStreamProps = {
    streamId: string;
    muted: boolean;
    loop: boolean;
    autoPlay: boolean;
    load: boolean;
    limitQualityToElementSize: boolean;
    limitBuffer?: boolean;
    dropAudio?: boolean;
    captions?: boolean;
    controls?: boolean;
} & Omit<VideoHTMLAttributes<HTMLVideoElement>, 'preload'>;

export const useVideoStream = ({
    streamId,
    muted,
    loop,
    autoPlay,
    load,
    limitQualityToElementSize,
    limitBuffer = false,
    dropAudio = false,
    captions = false,
    controls = false,
    ...rest
}: UseVideoStreamProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const playerRef = useRef<VideoPlayer | null>(null);
    const [hasLoaded, setHasLoaded] = useState(false);
    const hasInteractedIfNeeded = useHasInteractedIfNeeded();

    // Controls are only initially shown if explicitly set and autoPlay is not enabled
    // If autoPlay is enabled, controls are shown after our pseudo autoplay functionality below
    const [showControls, setShowControls] = useState(() => (controls ? !autoPlay : false));

    useEffect(() => {
        let frameId: number;
        if (!load || !hasInteractedIfNeeded) return undefined;

        const initialize = () => {
            if (videoRef.current) {
                const player = new VideoPlayer(videoRef.current, streamId, {
                    limitQualityToElementSize,
                    limitBuffer,
                    retainWholeClip: autoPlay && loop,
                    dropAudio,
                });
                playerRef.current = player;

                player.init(() => {
                    startTransition(() => {
                        setHasLoaded(true);
                    });
                });
            } else {
                frameId = requestAnimationFrame(initialize);
            }
        };

        initialize();

        return () => {
            cancelAnimationFrame(frameId);
            playerRef.current?.destroy();
            playerRef.current = null;
            setHasLoaded(false);
        };
    }, [autoPlay, dropAudio, hasInteractedIfNeeded, limitBuffer, limitQualityToElementSize, load, loop, streamId]);

    useEffect(() => {
        if (!playerRef.current) return;
        playerRef.current.mute(muted);
    }, [muted]);

    useEffect(() => {
        // videoRef.current is null by the time the cleanup runs, so hold the element here.
        const videoElement = videoRef.current;
        if (!videoElement || !playerRef.current || !hasLoaded || !loop) return undefined;

        const handleTimeUpdate = () => {
            const currentTime = playerRef.current?.getCurrentTime() ?? 0;
            const duration = playerRef.current?.getDuration() ?? 0;

            if (duration - currentTime < 0.2) {
                playerRef.current?.seek(0);
                playerRef.current?.play();
            }
        };

        videoElement.ontimeupdate = handleTimeUpdate;
        videoElement.onended = () => {
            // fallback for if the timeupdate event handler doesn't work
            playerRef.current?.seek(0);
            playerRef.current?.play();
        };

        return () => {
            videoElement.ontimeupdate = null;
            videoElement.onended = null;
        };
    }, [hasLoaded, loop]);

    useEffect(() => {
        // A looping preview keeps decoding and re-fetching segments on a hidden page.
        let pausedWhileHidden = false;

        const handleVisibilityChange = () => {
            const player = playerRef.current;
            if (!player) return;

            if (document.hidden) {
                if (player.isPaused()) return;
                pausedWhileHidden = true;
                player.pause();
            } else if (pausedWhileHidden) {
                pausedWhileHidden = false;
                player.play();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    useEffect(() => {
        if (!hasLoaded || !autoPlay || !playerRef.current) return;
        // This is a pseudo autoplay functionality that seems to perform more consistently
        // across different devices.
        playerRef.current.mute(true);
        playerRef.current.play(true);
        playerRef.current.mute(muted);
        playerRef.current.enableCaptions(captions);
        setShowControls(controls);
    }, [hasLoaded, autoPlay, captions, controls, muted]);

    const videoProps = useMemo(
        () => ({
            ref: videoRef,
            controls: showControls,
            loop,
            muted,
            ...rest,
        }),
        [showControls, loop, muted, rest],
    );

    return [playerRef, videoProps, hasLoaded] as const;
};
